/**
 * F4.0 — Comune scoring engine.
 *
 * Scores a single ComuneFull against a weighted profile. Returns a 0..100
 * integer score plus a per-dimension breakdown for the DetailsPanel.
 *
 * Dimensions: climate, cost, infra, foreign, tax.
 * Where data is missing the dimension is excluded from the weighted average
 * (not zeroed) so gaps don't collapse the score.
 */

import type { ComuneFull } from '@/data';

export type ScoringDimension = 'climate' | 'cost' | 'infra' | 'foreign' | 'tax';

/** Weights: positive real numbers. Relative scale — they are normalised. */
export type ProfileWeights = Record<ScoringDimension, number>;

/**
 * Per-dimension component scores (0..1) plus explanatory labels.
 * Null means the dimension had no data.
 */
export type ScoreComponents = Partial<Record<ScoringDimension, number>>;

export interface ComuneScoreResult {
  score: number;          // 0..100, rounded integer
  components: ScoreComponents;
  topStrength: ScoringDimension | null;
  topWeakness: ScoringDimension | null;
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, isNaN(v) ? 0 : v));
}

// Linear taper: full score at or below `low`, zero at or above `high`.
function taperDown(v: number, low: number, high: number): number {
  if (v <= low) return 1;
  if (v >= high) return 0;
  return (high - v) / (high - low);
}

// Linear taper: zero at or below `low`, full score at or above `high`.
function taperUp(v: number, low: number, high: number): number {
  if (v <= low) return 0;
  if (v >= high) return 1;
  return (v - low) / (high - low);
}

/**
 * Weighted mean over the parts that actually have data. A part with `v === null`
 * drops out of BOTH the numerator and the denominator, so a data gap never reads
 * as "scored zero on this axis" — the remaining parts are simply renormalised.
 * Returns null when nothing at all is known.
 */
function weightedMean(parts: Array<{ w: number; v: number | null }>): number | null {
  let weightSum = 0;
  let valueSum = 0;
  for (const p of parts) {
    if (p.v === null) continue;
    weightSum += p.w;
    valueSum += p.w * p.v;
  }
  return weightSum > 0 ? valueSum / weightSum : null;
}

// ─── climate ──────────────────────────────────────────────────────────────────

function scoreClimate(c: ComuneFull): number | null {
  // t_mean: prefer 8–16°C; taper out toward cold (<2°C) and hot (>22°C).
  const tMean = (() => {
    const t = c.climate.t_mean;
    if (t < 2) return taperUp(t, -5, 2);
    if (t > 22) return taperDown(t, 22, 30);
    return 1 - Math.abs(t - 12) / 12;
  })();

  // t_jul: penalise above 26°C, hard cap at 36°C.
  const tJul = taperDown(c.climate.t_jul, 26, 36);

  // t_jan: penalise below -3°C, floor at -15°C.
  const tJan = taperUp(c.climate.t_jan, -15, -3);

  // humidity: prefer <70%; penalty grows toward 90%. Not every country's
  // dataset carries it — unknown humidity drops out of the average instead of
  // scoring 0 (0% humidity is not a physical reading, it's a gap).
  const hum = c.climate.hum != null ? taperDown(c.climate.hum, 65, 90) : null;

  // sunshine: more is better (200→1500 hours range for North Italy).
  const sun = c.climate.sun != null ? taperUp(c.climate.sun, 200, 1500) : null;

  // Weighted average — t_mean and t_jul matter most.
  const avg = weightedMean([
    { w: 0.25, v: tMean },
    { w: 0.30, v: tJul },
    { w: 0.20, v: tJan },
    { w: 0.15, v: hum },
    { w: 0.10, v: sun },
  ]);
  return avg === null ? null : clamp01(avg);
}

// ─── cost ─────────────────────────────────────────────────────────────────────

function scoreCost(c: ComuneFull): number | null {
  const econ = c.econ;
  if (!econ) return null;

  const parts: number[] = [];

  if (econ.rent_eur_m2y != null) {
    // Rent €/m²/year: <60 great, >200 bad.
    parts.push(taperDown(econ.rent_eur_m2y, 60, 200));
  }
  if (econ.sale_eur_m2 != null) {
    // Sale €/m²: <1200 great, >5000 bad.
    parts.push(taperDown(econ.sale_eur_m2, 1200, 5000));
  }

  if (parts.length === 0) return null;
  return clamp01(parts.reduce((a, b) => a + b, 0) / parts.length);
}

// ─── infrastructure ───────────────────────────────────────────────────────────

function scoreInfra(c: ComuneFull): number | null {
  // Hospital: <5km great, >40km bad.
  const hosp = taperDown(c.infra.hosp, 5, 40);

  // Train: <3km great, >25km bad.
  const train = taperDown(c.infra.train, 3, 25);

  // School: <2km great, >15km bad.
  const school = taperDown(c.infra.school, 2, 15);

  // Internet: >100 Mbps great, <10 bad. Broadband telemetry is licensed and
  // exists only for Italy today — where it's missing the axis is renormalised
  // away, because 0 Mbps would mean "no connectivity", not "not measured".
  const inet = c.internet != null ? taperUp(c.internet.dl, 10, 150) : null;

  const avg = weightedMean([
    { w: 0.35, v: hosp },
    { w: 0.25, v: train },
    { w: 0.20, v: school },
    { w: 0.20, v: inet },
  ]);
  return avg === null ? null : clamp01(avg);
}

// ─── foreign (RU/UA/BY linguistic proximity) ──────────────────────────────────

function scoreForeign(c: ComuneFull): number | null {
  const f = c.foreign;
  if (!f) return null;

  // Sum of RU+UA+BY percentage (≤3% each, share of population).
  const slavicPct = (f.ru_pct ?? 0) + (f.ua_pct ?? 0) + (f.by_pct ?? 0);
  if (slavicPct === 0 && f.total == null) return null;

  // 0.5% = decent, 2%+ = great for finding a community.
  const slavic = taperUp(slavicPct, 0, 2.5);

  // General foreigner-friendliness proxy: >10% foreign = expat-friendly.
  const total = f.total != null ? taperUp(f.total, 2, 15) : 0.3;

  return clamp01(0.7 * slavic + 0.3 * total);
}

// ─── tax (IRPEF municipal addend) ─────────────────────────────────────────────

function scoreTax(c: ComuneFull): number | null {
  const irpef = c.tax?.irpef_pct;
  if (irpef == null) return null;
  // 0% = 1.0, 0.8% = 0.0 (typical Italian municipal range 0–0.8%).
  return clamp01(1 - irpef / 0.8);
}

// ─── main ─────────────────────────────────────────────────────────────────────

const SCORERS: Record<ScoringDimension, (c: ComuneFull) => number | null> = {
  climate: scoreClimate,
  cost: scoreCost,
  infra: scoreInfra,
  foreign: scoreForeign,
  tax: scoreTax,
};

export function scoreComune(
  comune: ComuneFull,
  weights: ProfileWeights,
): ComuneScoreResult {
  const components: ScoreComponents = {};

  for (const dim of Object.keys(SCORERS) as ScoringDimension[]) {
    const raw = SCORERS[dim](comune);
    if (raw !== null) components[dim] = clamp01(raw);
  }

  // Weighted average over available dimensions only.
  let weightSum = 0;
  let scoreSum = 0;
  for (const dim of Object.keys(components) as ScoringDimension[]) {
    const w = weights[dim] ?? 0;
    weightSum += w;
    scoreSum += w * (components[dim] ?? 0);
  }

  const raw = weightSum > 0 ? scoreSum / weightSum : 0;
  const score = Math.round(clamp01(raw) * 100);

  // Top strength = dimension with highest component score × weight.
  const dims = Object.keys(components) as ScoringDimension[];
  const ranked = dims
    .map((d) => ({ d, v: (components[d] ?? 0) * (weights[d] ?? 1) }))
    .sort((a, b) => b.v - a.v);

  return {
    score,
    components,
    topStrength: ranked[0]?.d ?? null,
    topWeakness: ranked[ranked.length - 1]?.d ?? null,
  };
}
